import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useCallback, useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/TimeEntryMirror/Add";
import { TableData } from "@/App/TimeEntryMirror/Table";
import { getTimeEntryMirrorData } from "@/Api/timeEntryMirror";
import { usePermissions } from "@/hooks/usePermissions";
import { UnityFilter, useUnityFilter } from "@/components/UnityFilter";
import { If } from "@/components/If";
import { RefreshCw, FileText, Pause, Play } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";

const AUTO_REFRESH_INTERVAL = 15; // segundos

export type TableDataProps = {
  id: number;
  uuid: string;
  unity_id: number | null;
  employee_id: number | null;
  requested_by_user_id: number;
  date_start: string;
  date_end: string;
  status: number;
  total_employees: number;
  processed_employees: number;
  error_message: string | null;
  created_at: string;
  unity: { id: number; name: string } | null;
  employee: { id: number; user: { id: number; name: string } } | null;
  requested_by: { id: number; name: string };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_INTERVAL);
  const { hasPermission } = usePermissions();
  const unityFilter = useUnityFilter();
  const unityParam = unityFilter.param;

  const fetchData = useCallback(
    async (page?: number) => {
      const { data } = await getTimeEntryMirrorData({ page, unity_id: unityParam });
      setListData(data?.response);
    },
    [unityParam]
  );

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    setCountdown(AUTO_REFRESH_INTERVAL);
    await fetchData(listData?.current_page || 1);
    setIsRefreshing(false);
  }, [fetchData, listData?.current_page]);

  useEffect(() => {
    if (!unityFilter.ready) return;
    (async () => {
      setState("loading");
      await fetchData();
      setState("production");
    })();
  }, [fetchData, unityFilter.ready]);

  // Auto-refresh com countdown — solicitações ficam Pendente/Processando até o
  // worker da fila terminar, então a tela precisa se atualizar sozinha.
  useEffect(() => {
    if (isPaused) return;

    const countdownInterval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleRefresh();
          return AUTO_REFRESH_INTERVAL;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [handleRefresh, isPaused]);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");
        const { data } = await getTimeEntryMirrorData({ page: listData?.current_page + 1, unity_id: unityParam });
        setListData(data.response);
        setState("production");
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");
        const { data } = await getTimeEntryMirrorData({ page: listData?.current_page - 1, unity_id: unityParam });
        setListData(data.response);
        setState("production");
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");
        const { data } = await getTimeEntryMirrorData({ page: listData?.last_page, unity_id: unityParam });
        setListData(data?.response);
        setState("production");
      }
      return;
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-time-entry-mirror-list")}>
        <PageHeader
          icon={FileText}
          title="Espelho de Ponto"
          description="Gere e acompanhe os relatórios de espelho de ponto dos colaboradores"
        >
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex items-center gap-1">
            <button
              onClick={() => setIsPaused(!isPaused)}
              className={cn(
                "p-2 rounded-md transition-colors",
                isPaused
                  ? "bg-yellow-500 text-white"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
              title={isPaused ? "Retomar auto-refresh" : "Pausar auto-refresh"}
            >
              {isPaused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={cn(
                "flex items-center gap-1.5 px-2.5 py-1.5 rounded-md transition-colors text-sm",
                isRefreshing
                  ? "text-gray-400"
                  : "text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
              )}
              title="Atualizar agora"
            >
              <RefreshCw className={cn("h-4 w-4", isRefreshing && "animate-spin")} />
              {!isPaused && !isRefreshing && (
                <span className="tabular-nums text-xs font-medium">{countdown}s</span>
              )}
            </button>
          </div>
          <If condition={hasPermission("create-time-entry-mirror")}>
            <AddBtn updateFront={() => fetchData(1)} />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <UnityFilter filter={unityFilter} />
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData dataApi={listData} pageActions={pageActions} updateFront={setListData} />
          )}
        </div>
      </If>
    </Main>
  );
};
